import {
  LoginRequest,
  LoginResponse,
  RefreshTokenRequest,
  RefreshTokenResponse,
  AuthState,
  UserInfo
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api/v1';
const AUTH_STORAGE_KEY = 'authState';

class AuthService {
  private static instance: AuthService;

  private constructor() { }

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(credentials),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.message || `Login failed: ${response.status}`);
    }

    const loginResponse: LoginResponse = await response.json();

    // Store auth state
    this.setAuthState({
      accessToken: loginResponse.accessToken,
      refreshToken: loginResponse.refreshToken,
      tokenType: loginResponse.tokenType,
      user: loginResponse.user,
    });

    return loginResponse;
  }

  async refreshToken(): Promise<RefreshTokenResponse> {
    const authState = this.getAuthState();
    if (!authState?.refreshToken) {
      throw new Error('No refresh token available');
    }

    const request: RefreshTokenRequest = {
      refreshToken: authState.refreshToken,
    };

    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      // If refresh fails, logout the user
      this.logout();
      throw new Error('Token refresh failed');
    }

    const refreshResponse: RefreshTokenResponse = await response.json();

    // Update stored tokens
    const currentState = this.getAuthState();
    if (currentState) {
      this.setAuthState({
        ...currentState,
        accessToken: refreshResponse.accessToken,
        tokenType: refreshResponse.tokenType,
      });
    }

    return refreshResponse;
  }

  logout(): void {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    // Also remove legacy key if present
    localStorage.removeItem('currentUser');
  }

  isAuthenticated(): boolean {
    const authState = this.getAuthState();
    return !!authState?.accessToken;
  }

  getAccessToken(): string | null {
    const authState = this.getAuthState();
    return authState?.accessToken || null;
  }

  getAuthorizationHeader(): string | null {
    const authState = this.getAuthState();
    if (!authState?.accessToken) {
      return null;
    }
    return `${authState.tokenType} ${authState.accessToken}`;
  }

  getCurrentUser(): UserInfo | null {
    const authState = this.getAuthState();
    return authState?.user || null;
  }

  getAuthState(): AuthState | null {
    try {
      const stored = localStorage.getItem(AUTH_STORAGE_KEY);
      if (!stored) {
        return null;
      }
      return JSON.parse(stored) as AuthState;
    } catch {
      return null;
    }
  }

  private setAuthState(state: AuthState): void {
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(state));
  }

  // Helper to check if token is expired (basic check based on JWT structure)
  isTokenExpired(): boolean {
    const token = this.getAccessToken();
    if (!token) {
      return true;
    }

    try {
      // Decode JWT payload (base64)
      const payload = token.split('.')[1];
      if (!payload) {
        return true;
      }

      const decoded = JSON.parse(atob(payload));
      if (!decoded.exp) {
        return false; // No expiration claim
      }

      // Check if expired (with 30 second buffer)
      const expirationTime = decoded.exp * 1000;
      return Date.now() >= expirationTime - 30000;
    } catch {
      return true;
    }
  }

  // Helper to get full display name
  getUserDisplayName(): string {
    const user = this.getCurrentUser();
    if (!user) {
      return '';
    }

    if (user.firstName || user.lastName) {
      return `${user.firstName || ''} ${user.lastName || ''}`.trim();
    }

    return user.email;
  }
}

export const authService = AuthService.getInstance();
